export type Lang = 'es' | 'en'

export interface TermsSection {
  heading: string
  body:    string[]
}

export interface TermsLocale {
  effectiveDate: string
  intro:         string
  sections:      TermsSection[]
}

export const termsData: Record<Lang, TermsLocale> = {
  es: {
    effectiveDate: 'Fecha de entrada en vigor: 24 de abril de 2026',
    intro:
      'Bienvenido/a a Tengo de To. Al crear una cuenta y utilizar nuestros servicios, aceptas los presentes Términos y Condiciones. Por favor, léelos detenidamente.',
    sections: [
      {
        heading: '1. Descripción del servicio',
        body: [
          'Tengo de To es una plataforma de estimulación cognitiva que ofrece actividades y juegos diseñados para el bienestar mental de personas mayores.',
          'El servicio se presta a través de la aplicación web y puede incluir funcionalidades adicionales según el plan de suscripción contratado.',
        ],
      },
      {
        heading: '2. Registro y cuenta de usuario',
        body: [
          'Para acceder al servicio es necesario crear una cuenta proporcionando una dirección de correo electrónico válida y una contraseña.',
          'Eres responsable de mantener la confidencialidad de tus credenciales y de todas las actividades realizadas bajo tu cuenta.',
          'Debes notificarnos de inmediato si sospechas de un acceso no autorizado a tu cuenta.',
        ],
      },
      {
        heading: '3. Uso aceptable',
        body: [
          'Te comprometes a utilizar el servicio únicamente con fines lícitos y de acuerdo con estos Términos.',
          'Queda prohibido compartir, revender o redistribuir el acceso al servicio sin autorización expresa.',
          'No está permitido el uso del servicio para actividades que puedan causar daño a terceros o a la plataforma.',
        ],
      },
      {
        heading: '4. Suscripciones y pagos',
        body: [
          'Algunos contenidos y funcionalidades están disponibles únicamente en planes de pago (Básico, Plus, Premium).',
          'Los precios y condiciones de cada plan se muestran en la página de suscripción. Los cargos se realizan a través de Stripe y son procesados de forma segura.',
          'Las suscripciones se renuevan automáticamente al final de cada período. Puedes cancelar en cualquier momento desde tu perfil.',
        ],
      },
      {
        heading: '5. Propiedad intelectual',
        body: [
          'Todos los contenidos, diseños y funcionalidades de la plataforma son propiedad de Tengo de To o de sus licenciantes y están protegidos por las leyes de propiedad intelectual.',
          'Se te concede una licencia limitada, no exclusiva e intransferible para acceder y utilizar el servicio para uso personal.',
        ],
      },
      {
        heading: '6. Privacidad y protección de datos',
        body: [
          'El tratamiento de tus datos personales se rige por nuestra Política de Privacidad, que forma parte integrante de estos Términos.',
          'Recopilamos únicamente los datos necesarios para prestar el servicio y no los vendemos a terceros.',
          'Tienes derecho a acceder, rectificar, suprimir y portar tus datos. Para ejercer estos derechos, contacta con nosotros en el correo indicado al final de este documento.',
        ],
      },
      {
        heading: '7. Limitación de responsabilidad',
        body: [
          'Tengo de To no garantiza que el servicio esté disponible de forma ininterrumpida o libre de errores.',
          'Las actividades ofrecidas tienen un propósito de estimulación cognitiva y entretenimiento, y no constituyen diagnóstico, tratamiento ni consejo médico.',
          'En la máxima medida permitida por la ley, nuestra responsabilidad total no superará el importe abonado por el usuario en los doce meses anteriores al hecho causante.',
        ],
      },
      {
        heading: '8. Modificación de los términos',
        body: [
          'Nos reservamos el derecho de modificar estos Términos en cualquier momento. Te notificaremos los cambios relevantes por correo electrónico o mediante un aviso en la aplicación.',
          'El uso continuado del servicio tras la notificación implica la aceptación de los nuevos Términos.',
        ],
      },
      {
        heading: '9. Legislación aplicable',
        body: [
          'Estos Términos se rigen por la legislación española y, en lo que corresponda, por la normativa de la Unión Europea.',
          'Cualquier controversia se someterá a los juzgados y tribunales del domicilio del usuario, salvo que la ley establezca otro fuero.',
        ],
      },
      {
        heading: '10. Contacto',
        body: [
          'Si tienes preguntas sobre estos Términos, puedes escribirnos a: soporte@tengodeto.net',
        ],
      },
    ],
  },
  en: {
    effectiveDate: 'Effective date: April 24, 2026',
    intro:
      'Welcome to Tengo de To. By creating an account and using our services, you agree to these Terms and Conditions. Please read them carefully.',
    sections: [
      {
        heading: '1. Description of service',
        body: [
          'Tengo de To is a cognitive stimulation platform offering activities and games designed for the mental well-being of older adults.',
          'The service is provided via the web application and may include additional features depending on the subscription plan selected.',
        ],
      },
      {
        heading: '2. Registration and user account',
        body: [
          'To access the service you must create an account by providing a valid email address and a password.',
          'You are responsible for keeping your credentials confidential and for all activities carried out under your account.',
          'You must notify us immediately if you suspect unauthorized access to your account.',
        ],
      },
      {
        heading: '3. Acceptable use',
        body: [
          'You agree to use the service only for lawful purposes and in accordance with these Terms.',
          'Sharing, reselling or redistributing access to the service without express authorization is prohibited.',
          'Using the service for activities that could harm third parties or the platform is not permitted.',
        ],
      },
      {
        heading: '4. Subscriptions and payments',
        body: [
          'Some content and features are only available on paid plans (Basic, Plus, Premium).',
          'Prices and conditions for each plan are shown on the subscription page. Charges are processed securely through Stripe.',
          'Subscriptions renew automatically at the end of each period. You can cancel at any time from your profile.',
        ],
      },
      {
        heading: '5. Intellectual property',
        body: [
          'All content, designs and features of the platform are the property of Tengo de To or its licensors and are protected by intellectual property laws.',
          'You are granted a limited, non-exclusive, non-transferable licence to access and use the service for personal use.',
        ],
      },
      {
        heading: '6. Privacy and data protection',
        body: [
          'The processing of your personal data is governed by our Privacy Policy, which forms an integral part of these Terms.',
          'We collect only the data necessary to provide the service and do not sell it to third parties.',
          'You have the right to access, rectify, delete and port your data. To exercise these rights, contact us at the address shown at the end of this document.',
        ],
      },
      {
        heading: '7. Limitation of liability',
        body: [
          'Tengo de To does not guarantee that the service will be available without interruption or free of errors.',
          'The activities offered are intended for cognitive stimulation and entertainment only and do not constitute medical diagnosis, treatment or advice.',
          'To the maximum extent permitted by law, our total liability will not exceed the amount paid by the user in the twelve months prior to the event giving rise to the claim.',
        ],
      },
      {
        heading: '8. Changes to these terms',
        body: [
          'We reserve the right to modify these Terms at any time. We will notify you of material changes by email or via an in-app notice.',
          'Continued use of the service after notification constitutes acceptance of the updated Terms.',
        ],
      },
      {
        heading: '9. Governing law',
        body: [
          'These Terms are governed by Spanish law and, where applicable, by European Union regulations.',
          'Any dispute shall be submitted to the courts of the user\'s place of residence, unless the law provides otherwise.',
        ],
      },
      {
        heading: '10. Contact',
        body: [
          'If you have questions about these Terms, you can reach us at: soporte@tengodeto.net',
        ],
      },
    ],
  },
}
